'use client';
import React, { useRef, createContext, useContext } from 'react';
import { createStore } from 'zustand/vanilla';
import { useStore } from 'zustand';
import { createJSONStorage, persist } from 'zustand/middleware';
import {
  clearPersistedUserState,
  USER_STATE_STORAGE_KEY,
} from './userStateStorage';

export type IState = {
  currentIdentity: { teamId?: string; teamType?: number };
  // 是否有团队
  hasTeam: boolean;
  // 登录用户信息
  info: {
    GUID?: string;
    Name?: string;
    Avatar?: string;
    Phone?: string;
    Mobile?: string;
    IDCard?: {
      Type: number | null;
      Name?: string;
      Number?: string;
    };
    Department?: string;
    Position?: string;
  };
  // 是否实名
  real_status: boolean;
  // 用户展示的字段
  userName: string;
  // 是否登录
  is_login: boolean;
  // 是否跳过实名
  is_skip_real_name: boolean;
  setState: (v: Omit<IState, 'setState'> | Function) => void | IState;
  clear(): void;
  // 是否访问过AI工具箱
  is_visit_ai_tool?: boolean;
};

type LocalGlobalStoreApi = ReturnType<typeof createLocalGlobalStore>;

const initialState: IState = {
  info: {},
  currentIdentity: {},
  hasTeam: false,
  real_status: false,
  is_login: false,
  is_skip_real_name: false,
  is_visit_ai_tool: false,
  userName: '',
  setState() {},
  clear() {},
};

const createLocalGlobalStore = (initialState: IState) => {
  return createStore<IState>()(
    persist(
      (set, get) => ({
        ...initialState,
        setState(v: Omit<IState, 'setState'> | Function) {
          const all = get();
          if (typeof v == 'function') {
            set({ ...v(all), setState: all.setState, clear: all.clear });
          } else {
            set({ ...v, setState: all.setState, clear: all.clear });
          }
        },
        clear() {
          const all = get();
          clearPersistedUserState();
          set({ ...initialState, setState: all.setState, clear: all.clear });
        },
      }),
      {
        name: USER_STATE_STORAGE_KEY,
        storage: createJSONStorage(() => {
          return localStorage;
        }),
        version: 1,
      }
    )
  );
};

export const LocalGlobalContext = createContext<
  LocalGlobalStoreApi | undefined
>(undefined);

export const LocalGlobalContextProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const storeRef = useRef<LocalGlobalStoreApi>();
  if (!storeRef.current) {
    storeRef.current = createLocalGlobalStore(initialState);
  }
  return (
    <LocalGlobalContext.Provider value={storeRef.current}>
      {children}
    </LocalGlobalContext.Provider>
  );
};

/**
 * @Deprecated  replace useGlobal, future remove
 * @param selector
 * @returns
 */
export const useLocalGlobalStore = <T,>(selector: (store: IState) => T): T => {
  const localGlobalStoreContext = useContext(LocalGlobalContext);

  if (!localGlobalStoreContext) {
    throw new Error(
      `useLocalGlobalStore must be used within LocalGobalProvider`
    );
  }

  return useStore(localGlobalStoreContext, selector);
};
